'use client';

import { c as _c } from "react/compiler-runtime";
import { LayoutContainer, Section } from '@petsmart-ui/sparky';
import cn from 'classnames';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import type { FC } from 'react';
import { useSitePreviewData } from '@libs/common-next';
import type { Offer } from '@libs/common-types';
import type { CardUpVariantType } from '../../types';
import { getPlpModuleSectionHeading, getTrimmedDisplayTitle } from '../../utils/plpModuleSectionHeading';
import { ColoredTextLink } from '../ColoredTextLink';
import { trackCardUpLinkActionClick } from './analytics';
import CardUpVariant from './CardUpVariant';
import { OfferPattern } from './offer-pattern';
import type { Card, CardUpBrandProps, CardUpOfferProps, CardUpProps } from './types';
import { prepareEligibleOffers } from './utils';
import './CardInfo.scss';
import './CardUp.scss';
import './CardUpArticle.scss';
const CardUpArticleVariant = dynamic(() => import('./CardUpArticleVariant'), {
  ssr: false
});
const CardUpBrandVariant = dynamic(() => import('./CardUpBrandVariant'), {
  ssr: false
});
const CardUpOfferVariant = dynamic(() => import('./card-up-offer-variant'));
const ComponentCardUp: Record<CardUpVariantType, FC<CardUpProps>> = {
  '2up': props => <CardUpVariant sliceMax={2} {...props} />,
  '2up Brand': props => <CardUpBrandVariant sliceMax={2} {...props as unknown as CardUpBrandProps} />,
  '3up': props => <CardUpVariant sliceMax={3} {...props} />,
  '4up': props => <CardUpVariant sliceMax={4} {...props} />,
  '4Stack': props => <CardUpVariant sliceMax={4} {...props} />,
  '3up Article': props => <CardUpArticleVariant articlesPerView={3} {...props} />,
  Info: props => <CardUpVariant sliceMax={4} {...props} />,
  Offer: props => <CardUpOfferVariant {...props as unknown as CardUpOfferProps} />
};
const validateCardTypes = (cards: Card[]): boolean => {
  const articleModel = cards.some(card => card.previewDescription);
  const articleModelsAll = cards.every(card => card.previewDescription);
  return articleModel && !articleModelsAll;
};
const getEligibleCards = (variant: CardUpVariantType, cards: Card[], previewDate?: string): Card[] | Offer[] => {
  if (variant === 'Offer') {
    return prepareEligibleOffers(cards ?? [], previewDate ? new Date(previewDate) : new Date());
  }
  return cards;
};
export default function CardUp(props) {
  const $ = _c(4);
  if (props.variant === "Offer") {
    let t0;
    if ($[0] !== props) {
      t0 = <OfferPattern><CardUpSection {...props} /></OfferPattern>;
      $[0] = props;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  }
  let t0;
  if ($[2] !== props) {
    t0 = <CardUpSection {...props} />;
    $[2] = props;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
}
function CardUpSection(t0) {
  const $ = _c(55);
  const {
    header,
    description,
    linkAction,
    linkActionColor,
    variant,
    cards,
    backgroundColor,
    isH1
  } = t0;
  const pathname = usePathname();
  const previewDate = useSitePreviewData()?.previewDate;
  let t1;
  if ($[0] !== cards || $[1] !== previewDate || $[2] !== variant) {
    t1 = getEligibleCards(variant, cards, previewDate);
    $[0] = cards;
    $[1] = previewDate;
    $[2] = variant;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const cardsToRender = t1;
  if (!cardsToRender?.length) {
    return null;
  }
  const Component = ComponentCardUp[variant];
  if (!Component) {
    return null;
  }
  if (variant === "3up Article" && validateCardTypes(cards)) {
    return null;
  }
  let t2;
  if ($[4] !== linkAction) {
    t2 = linkAction || {};
    $[4] = linkAction;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    link,
    actionText
  } = t2;
  let t3;
  if ($[6] !== header) {
    t3 = getTrimmedDisplayTitle(header);
    $[6] = header;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const displayHeader = t3;
  let t4;
  if ($[8] !== header || $[9] !== isH1) {
    t4 = getPlpModuleSectionHeading(header, isH1);
    $[8] = header;
    $[9] = isH1;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const {
    title: sectionTitle,
    headingTagName
  } = t4;
  let t5;
  if ($[11] !== actionText || $[12] !== cardsToRender || $[13] !== displayHeader || $[14] !== header || $[15] !== link || $[16] !== linkAction || $[17] !== linkActionColor || $[18] !== pathname || $[19] !== variant) {
    t5 = displayHeader && link && actionText ? <ColoredTextLink data-testid="link-action" onClick={() => trackCardUpLinkActionClick({
      header,
      linkAction,
      offerCount: cardsToRender.length,
      pathname: pathname ?? "",
      variant
    })} href={link?.webUrl} color={linkActionColor}>{actionText}</ColoredTextLink> : null;
    $[11] = actionText;
    $[12] = cardsToRender;
    $[13] = displayHeader;
    $[14] = header;
    $[15] = link;
    $[16] = linkAction;
    $[17] = linkActionColor;
    $[18] = pathname;
    $[19] = variant;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  const titleAfter = t5;
  let isRestyled;
  let t6;
  let variantName;
  if ($[21] !== backgroundColor || $[22] !== displayHeader || $[23] !== variant) {
    variantName = variant.toLowerCase().replaceAll(" ", "-");
    const noHeaderClass = !displayHeader ? `${variantName}--no-header` : "";
    let t7;
    if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = ["2up", "3up", "3up Article", "4up", "Info", "4Stack", "Offer"];
      $[27] = t7;
    } else {
      t7 = $[27];
    }
    const restyledVariants = t7;
    isRestyled = restyledVariants.includes(variant);
    t6 = cn("card-up", variantName, noHeaderClass, isRestyled && "card-up--restyled", {
      "offer--with-background": variant === "Offer" && backgroundColor
    });
    $[21] = backgroundColor;
    $[22] = displayHeader;
    $[23] = variant;
    $[24] = isRestyled;
    $[25] = t6;
    $[26] = variantName;
  } else {
    isRestyled = $[24];
    t6 = $[25];
    variantName = $[26];
  }
  const componentClassName = t6;
  const cardHeaderTagName = isH1 || !displayHeader ? "h2" : "h3";
  const t7 = `card-up-container card-up-container--${variantName}`;
  let t8;
  if ($[28] !== backgroundColor) {
    t8 = {
      backgroundColor
    };
    $[28] = backgroundColor;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== isRestyled) {
    t9 = isRestyled && {
      align: "center",
      titleAfterLocation: "below-description"
    };
    $[30] = isRestyled;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  const t10 = cardsToRender as Card[];
  const t11 = pathname ?? "";
  let t12;
  if ($[32] !== Component || $[33] !== cardHeaderTagName || $[34] !== description || $[35] !== header || $[36] !== isH1 || $[37] !== linkAction || $[38] !== t10 || $[39] !== t11 || $[40] !== variant) {
    t12 = <Component header={header} description={description} linkAction={linkAction} cards={t10} variant={variant} pathname={t11} isH1={isH1} cardHeaderTagName={cardHeaderTagName} />;
    $[32] = Component;
    $[33] = cardHeaderTagName;
    $[34] = description;
    $[35] = header;
    $[36] = isH1;
    $[37] = linkAction;
    $[38] = t10;
    $[39] = t11;
    $[40] = variant;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  let t13;
  if ($[42] !== componentClassName || $[43] !== description || $[44] !== headingTagName || $[45] !== sectionTitle || $[46] !== t12 || $[47] !== t8 || $[48] !== t9 || $[49] !== titleAfter || $[50] !== variantName) {
    t13 = <Section data-testid="card-up" data-variant={variantName} className={componentClassName} title={sectionTitle} description={description} titleAfter={titleAfter} style={t8} headingTagName={headingTagName} {...t9}>{t12}</Section>;
    $[42] = componentClassName;
    $[43] = description;
    $[44] = headingTagName;
    $[45] = sectionTitle;
    $[46] = t12;
    $[47] = t8;
    $[48] = t9;
    $[49] = titleAfter;
    $[50] = variantName;
    $[51] = t13;
  } else {
    t13 = $[51];
  }
  let t14;
  if ($[52] !== t13 || $[53] !== t7) {
    t14 = <LayoutContainer className={t7}>{t13}</LayoutContainer>;
    $[52] = t13;
    $[53] = t7;
    $[54] = t14;
  } else {
    t14 = $[54];
  }
  return t14;
}